/**
 * Theme owned Notify Me button.
 *
 * The buy area renders the button in Liquid, so a shopper who lands straight on
 * a sold out variant already has the right buy state at first paint. This file
 * only keeps it in step afterwards.
 *
 * The single source of truth for "the selected variant cannot be bought" is the
 * disabled state of the Add to Cart button in the same buy area. Every path that
 * changes the variant - colour swatches, size swatches, the variant picker - goes
 * through that one attribute, so following it keeps the Notify Me button correct
 * without this file having to know about any of those paths.
 *
 * Hook for the sign up form that lands separately:
 *   - a cancelable `notify-me:open` event is dispatched on document with
 *     { variantId, productHandle, button } in detail; calling preventDefault on
 *     it suppresses the placeholder panel;
 *   - window.themeNotifyMe.setHandler(fn) registers a handler that is called
 *     with the same detail instead of the placeholder panel;
 *   - window.themeNotifyMe.sync(), .open(), .close() and .getContext() are
 *     available for anything that needs to drive the button directly.
 */
(function () {
  'use strict';

  // Quick view copies the buy area scripts into head on every open, and three
  // sections can render this file on one page, so a second run must not install
  // a second set of listeners - it only has to bring the new markup in step.
  if (window.themeNotifyMe && typeof window.themeNotifyMe.sync === 'function') {
    window.themeNotifyMe.sync();
    return;
  }

  var BUTTON_SELECTOR = '[data-notify-me-button]';
  var HOLDER_SELECTOR = '.add_to_cart_holder';
  var HOLDER_ACTIVE_CLASS = 'add_to_cart_holder--notify';
  var HOLDER_MULTI_CLASS = 'add_to_cart_holder--multi';
  var ADD_TO_CART_SELECTOR = '.single-add-to-cart-button';
  var DIALOG_SELECTOR = '[data-notify-me-dialog]';
  var BODY_OPEN_CLASS = 'notify-me-dialog-open';
  // The buy area, and anything a change in it can come from.
  var CHANGE_SCOPE_SELECTOR = 'form[data-type="add-to-cart-form"], product-form, fieldset.product-form__input, .solid_swatches, .print_swatches';

  var handler = null;
  var lastFocusedElement = null;
  // Set when Escape closed a panel, so the keyup of that same press is stopped.
  var swallowEscapeKeyup = false;
  var observer = null;
  var observedHolders = [];
  // Dialogs currently parked on <body>; see moveDialogToBody.
  var movedDialogs = [];

  function buttons() {
    return Array.prototype.slice.call(document.querySelectorAll(BUTTON_SELECTOR));
  }

  function firstButton() {
    return document.querySelector(BUTTON_SELECTOR);
  }

  function dialogFor(button) {
    var scope = button ? button.closest('product-form') : null;
    var scoped = scope && scope.querySelector(DIALOG_SELECTOR);
    if (scoped) return scoped;

    // An open dialog sits on <body> rather than in its buy area, so look it up
    // by the buy area it came from before falling back to the first on the page.
    var moved = movedDialogs.filter(function (entry) {
      return !scope || entry.origin === scope;
    })[0];
    return (moved && moved.dialog) || document.querySelector(DIALOG_SELECTOR);
  }

  // Quick view renders the buy area inside a transformed side panel, and a
  // transform makes that panel the containing block for position: fixed. Parked
  // on <body> the dialog covers the page the way it is meant to, and a click on
  // its overlay is a click on the dialog rather than outside the quick view.
  function moveDialogToBody(dialog) {
    if (!dialog || dialog.parentNode === document.body || !dialog.parentNode) return;
    movedDialogs.push({ dialog: dialog, origin: dialog.parentNode, next: dialog.nextSibling });
    document.body.appendChild(dialog);
  }

  function restoreDialog(dialog) {
    for (var index = 0; index < movedDialogs.length; index++) {
      if (movedDialogs[index].dialog !== dialog) continue;

      var entry = movedDialogs.splice(index, 1)[0];
      if (entry.origin && document.contains(entry.origin)) {
        var next = entry.next && entry.next.parentNode === entry.origin ? entry.next : null;
        entry.origin.insertBefore(dialog, next);
      } else if (dialog.parentNode) {
        // The buy area went away underneath it - quick view closed - so the
        // parked copy goes with it.
        dialog.parentNode.removeChild(dialog);
      }
      return;
    }
  }

  // Nothing calls close when quick view is dismissed, so drop any parked dialog
  // whose buy area has left the page.
  function dropOrphanedDialogs() {
    movedDialogs.slice().forEach(function (entry) {
      if (entry.origin && document.contains(entry.origin)) return;
      entry.dialog.setAttribute('hidden', '');
      setExpanded(entry.dialog, false);
      entry.dialog.notifyMeOpener = null;
      restoreDialog(entry.dialog);
    });
    if (!openDialogs().length) document.body.classList.remove(BODY_OPEN_CLASS);
  }

  // The variant the buy area is currently pointed at. The hidden id input is what
  // Add to Cart would post, so it is also what a notify sign up should be about.
  function selectedVariantId(button) {
    var form = button.closest('form') || document.querySelector('form[data-type="add-to-cart-form"]');
    var input = form ? form.querySelector('input[name="id"]') : null;
    return input && input.value ? input.value : button.getAttribute('data-variant-id');
  }

  function contextFor(button) {
    return {
      button: button,
      variantId: selectedVariantId(button),
      productHandle: button.getAttribute('data-product-handle')
    };
  }

  function getContext() {
    var button = firstButton();
    return button ? contextFor(button) : null;
  }

  // Show or hide the button, and take the express payment button out of the grid
  // for as long as the button is there.
  function sync() {
    var seenHolders = [];

    dropOrphanedDialogs();

    buttons().forEach(function (button) {
      var holder = button.closest(HOLDER_SELECTOR);
      if (!holder) return;

      var addToCart = holder.querySelector(ADD_TO_CART_SELECTOR);
      // ECSR-1470: a button greyed out only because no size has been chosen yet
      // is waiting for the shopper, not reporting a variant that cannot be bought.
      var unavailable = !!(addToCart && addToCart.hasAttribute('disabled') &&
        !addToCart.hasAttribute('data-awaiting-size'));
      var variantId = selectedVariantId(button);

      if (variantId) {
        button.setAttribute('data-variant-id', variantId);
      }

      if (unavailable) {
        button.removeAttribute('hidden');
        holder.classList.add(HOLDER_ACTIVE_CLASS);
        // Two buttons in the holder, so the grid has to be two up even when the
        // express payment button is switched off in the section settings.
        holder.classList.add(HOLDER_MULTI_CLASS);
      } else {
        button.setAttribute('hidden', '');
        holder.classList.remove(HOLDER_ACTIVE_CLASS);
        if (!holder.hasAttribute('data-dynamic-checkout')) {
          holder.classList.remove(HOLDER_MULTI_CLASS);
        }
        closeDialog(dialogFor(button));
      }

      seenHolders.push(holder);
    });

    // Only re-arm the observer when the set of holders on the page changed.
    if (seenHolders.length !== observedHolders.length ||
        seenHolders.some(function (holder, index) { return observedHolders[index] !== holder; })) {
      observedHolders = seenHolders;
      observeHolders();
    }
  }

  function scheduleSync() {
    sync();
    // The swatch handlers update the id input and the Add to Cart button in the
    // same turn as the event that announces the change, so look again after it.
    window.setTimeout(sync, 0);
  }

  // The button reports the state of the panel it owns, so aria-haspopup="dialog"
  // is answered by aria-expanded rather than left unreported.
  function setExpanded(dialog, expanded) {
    var opener = dialog && dialog.notifyMeOpener;
    if (opener) opener.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function openDialog(dialog, button) {
    if (!dialog || !dialog.hasAttribute('hidden')) return;
    // Only once the panel is about to be shown is there a focus worth returning to.
    lastFocusedElement = document.activeElement;
    moveDialogToBody(dialog);
    dialog.removeAttribute('hidden');
    if (button) dialog.notifyMeOpener = button;
    setExpanded(dialog, true);
    document.body.classList.add(BODY_OPEN_CLASS);
    // Focus lands on the first field the panel wants, and on its close control
    // when the panel has no field of its own.
    var autofocus = dialog.querySelector('[data-notify-me-autofocus]');
    if (autofocus && (autofocus.hasAttribute('hidden') || autofocus.offsetParent === null)) autofocus = null;
    var target = autofocus ||
      dialog.querySelector('[data-notify-me-dialog-close]:not(.product-notify-me-dialog--overlay)');
    if (target) target.focus();
  }

  // Every way of closing - the close control, the overlay, Escape, a variant
  // change that hides the button - comes through here, so the scroll lock is
  // released and the dialog is back in its buy area before focus is handed
  // back. Focus goes to the button that opened the panel, and only falls back
  // to whatever was focused before it when that button has left the page.
  function closeDialog(dialog) {
    if (!dialog || dialog.hasAttribute('hidden')) return;
    var opener = dialog.notifyMeOpener;
    dialog.setAttribute('hidden', '');
    setExpanded(dialog, false);
    dialog.notifyMeOpener = null;
    restoreDialog(dialog);
    if (!openDialogs().length) {
      document.body.classList.remove(BODY_OPEN_CLASS);
    }

    var returnTo = opener && document.contains(opener) && !opener.hasAttribute('hidden') ? opener : null;
    if (!returnTo && lastFocusedElement && document.contains(lastFocusedElement)) {
      returnTo = lastFocusedElement;
    }
    lastFocusedElement = null;
    if (returnTo) returnTo.focus();
  }

  function open(button) {
    var target = button || firstButton();
    if (!target) return;

    var context = contextFor(target);

    var event = new CustomEvent('notify-me:open', {
      detail: context,
      bubbles: true,
      cancelable: true
    });
    var notPrevented = document.dispatchEvent(event);

    if (typeof handler === 'function') {
      handler(context);
      return;
    }
    if (!notPrevented) return;

    openDialog(dialogFor(target), target);
  }

  function close() {
    openDialogs().forEach(function (dialog) {
      closeDialog(dialog);
    });
  }

  function observeHolders() {
    if (!observer) return;
    // Holders from a previous page state stay observed otherwise.
    observer.disconnect();
    observedHolders.forEach(function (holder) {
      observer.observe(holder, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['disabled', 'data-awaiting-size']
      });
    });
  }

  function onClick(event) {
    var trigger = event.target.closest ? event.target.closest(BUTTON_SELECTOR) : null;
    if (trigger) {
      event.preventDefault();
      open(trigger);
      return;
    }

    var closer = event.target.closest ? event.target.closest('[data-notify-me-dialog-close]') : null;
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest(DIALOG_SELECTOR));
    }
  }

  // Focus stays inside the open panel: the dialog says aria-modal, so Tab must
  // not walk out into the page behind it.
  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function openDialogs() {
    return Array.prototype.slice.call(document.querySelectorAll(DIALOG_SELECTOR)).filter(function (dialog) {
      return !dialog.hasAttribute('hidden');
    });
  }

  function trapFocus(event, dialog) {
    var panel = dialog.querySelector('.product-notify-me-dialog--panel') || dialog;
    var focusable = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (node) {
      return node.offsetParent !== null || node === document.activeElement;
    });
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (!panel.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    // The success message takes focus with tabindex="-1", so it is inside the
    // panel but not in the focusable list: without this, Shift+Tab from it
    // would walk out into the page behind the dialog.
    if (focusable.indexOf(active) === -1) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function isEscape(event) {
    return event.key === 'Escape' || event.key === 'Esc';
  }

  function onKeydown(event) {
    if (isEscape(event)) {
      if (!openDialogs().length) return;
      // The panel is the topmost layer, so nothing else on the page gets to
      // answer this Escape: the theme's side panel handler focuses the header
      // cart trigger for any Escape, which would take focus off the Notify Me
      // button and scroll the page up to the header.
      event.preventDefault();
      event.stopPropagation();
      swallowEscapeKeyup = true;
      close();
      return;
    }
    if (event.key !== 'Tab') return;

    var dialog = openDialogs()[0];
    if (dialog) trapFocus(event, dialog);
  }

  // The theme's side panel handler listens for Escape on keyup, so stopping the
  // keydown is not enough - the release of the same key press has to be stopped
  // too, or focus still jumps to the header cart trigger after the panel closed.
  function onKeyup(event) {
    if (!swallowEscapeKeyup) return;
    swallowEscapeKeyup = false;
    if (!isEscape(event)) return;
    event.stopPropagation();
  }

  // The buy area is the only place a change can alter the selected variant, so
  // there is no reason to look at every change event on the page.
  function onChange(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest(CHANGE_SCOPE_SELECTOR)) return;
    scheduleSync();
  }

  function init() {
    if (typeof MutationObserver !== 'undefined' && !observer) {
      observer = new MutationObserver(function () {
        sync();
      });
    }

    document.addEventListener('click', onClick);
    // Capture phase for both: propagation has to be stopped before the handlers
    // the theme registers on document ever see the key.
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('keyup', onKeyup, true);
    document.addEventListener('product:variant-changed', scheduleSync);
    document.addEventListener('change', onChange);
    // Back, forward and a restore from the back forward cache all have to leave a
    // buy area that matches the variant in the address bar.
    window.addEventListener('popstate', scheduleSync);
    window.addEventListener('pageshow', scheduleSync);

    sync();
  }

  window.themeNotifyMe = {
    version: 1,
    sync: sync,
    open: open,
    close: close,
    getContext: getContext,
    setHandler: function (fn) {
      handler = typeof fn === 'function' ? fn : null;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
